import {
  APIListValuesRequest,
  APIListValuesResponse,
  APIPaginatedRequest,
  getFetcher,
} from "shared/api/api";
import client from "shared/api/axios";
import { createURL } from "shared/api/utils";
import { MileageUnit } from "shared/types";

import { BaseSensorReadingTimelineEntry } from "./v2/api";

const SENSORS_BASE_ROUTE = "sensors";

type SensorType = "point-value" | "state";

export interface Sensor {
  ID: string;
  displayName: string;
  type: SensorType;
  createdAt: string;
  updatedAt: string;
  description: string | null;
  unit: string | null;
  valueLabels: Record<string | number, string> | null;
  published: boolean;
}

interface ListSensorsRequest extends APIPaginatedRequest {}

const listSensorsRequestURI = (params: ListSensorsRequest): string =>
  client.getUri({
    method: "GET",
    url: createURL([SENSORS_BASE_ROUTE]),
    params,
  });

export const getSensors = (args: ListSensorsRequest) =>
  getFetcher<Sensor[]>(listSensorsRequestURI(args));

const listSensorsReadingsValuesRequestURI = ({
  fieldName,
  ...otherParams
}: APIListValuesRequest): string =>
  client.getUri({
    method: "GET",
    url: createURL([SENSORS_BASE_ROUTE, "readings", "values", fieldName]),
    params: otherParams,
  });

const listSensorsValuesRequestURI = ({
  fieldName,
  ...otherParams
}: APIListValuesRequest): string =>
  client.getUri({
    method: "GET",
    url: createURL([SENSORS_BASE_ROUTE, "values", fieldName]),
    params: otherParams,
  });

export const listSensorsReadingValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listSensorsReadingsValuesRequestURI(args));

export const listSensorValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listSensorsValuesRequestURI(args));

export const getSensorsReadingsTimelineURI = ({
  skipRequest,
  ...params
}: SensorReadingsTimelineRequest): string =>
  skipRequest
    ? ""
    : client.getUri({
        method: "GET",
        url: createURL([SENSORS_BASE_ROUTE, "readings", "timeline"]),
        params,
      });

export const sensorReadingsTimelineGroupingOptions = [
  "day",
  "hour",
  "10minutes",
  "minute",
  "none",
] as const;
export type SensorReadingsTimelineGrouping =
  (typeof sensorReadingsTimelineGroupingOptions)[number];

export type SensorReadingsTimelineAggregation = "avg" | "min" | "max";

export interface SensorReadingsTimelineRequest {
  mileageUnit: MileageUnit;
  sensorReadingsFilter?: string;
  grouping?: SensorReadingsTimelineGrouping;
  aggregation?: SensorReadingsTimelineAggregation;
  skipRequest?: boolean;
}

export interface SensorReadingTimelineEntry extends BaseSensorReadingTimelineEntry {
  VIN: string;
}

export interface SensorReadingTimeline {
  data: SensorReadingTimelineEntry[];
  metadata?: Sensor[];
}

export interface LatestSensorReading {
  sensorID: string;
  reading: number;
  trigger: string | null;
  latestReadAt: string;
}

export interface ListLatestSensorsRequest extends APIPaginatedRequest {
  vin: string;
  sensorReadingsFilter?: string;
}

export const listLatestSensorsReadingsRequestURI = ({
  vin,
  ...params
}: ListLatestSensorsRequest): string =>
  client.getUri({
    method: "GET",
    url: createURL([SENSORS_BASE_ROUTE, "readings", vin, "latest"]),
    params,
  });
